import React, { useEffect, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { EXPENSE_CATEGORIES } from "@/config/orignalAppConfig.js";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { localizeDemo } from "@/i18n/orignalI18n.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMarcelMode } from "@/context/MarcelMode.jsx";

const HUNT_CATEGORIES = [
  ["essence_commune", "Essence"],
  ["nourriture", "Nourriture"],
  ["materiel", "Équipement"],
  ["boucherie", "Boucherie"],
  ["autre", "Autre dépense commune"],
];

export function ExpensesPage({ scope = "prospect" }) {
  const { ui, lang } = useOrignalUi();
  const { user } = useAuth();
  const { actingAsAdmin } = useMarcelMode();
  const [data, setData] = useState({ items: [], balances: {} });
  const [members, setMembers] = useState([]);
  const [ready, setReady] = useState(false);
  const [bilan, setBilan] = useState(null);
  const [form, setForm] = useState({ description: "", amount: "", category: "nourriture", payer_id: "" });
  const [receipt, setReceipt] = useState(null);

  async function reload() {
    const [exp, mem, boot] = await Promise.all([api("/api/expenses"), api("/api/members"), api("/api/bootstrap")]);
    setData(exp);
    setMembers(mem.items);
    setBilan(boot?.hunt?.prospection?.bilan || null);
    setReady(true);
    if (!form.payer_id && mem.items[0]) setForm((f) => ({ ...f, payer_id: mem.items[0].id }));
  }

  useEffect(() => { reload().catch(() => {}); }, []);

  const names = Object.fromEntries(members.map((m) => [m.id, m.display_name]));
  const official = members.filter((m) => (m.kind || "official") === "official");
  const meId = user?.member_id;
  const paidByMe = (data.items || []).filter((e) => e.payer_id === meId && e.shared !== false).reduce((s, e) => s + Number(e.amount || 0), 0);
  const myShare = data.balances?.per_person ?? 0;
  const balance = paidByMe - myShare;
  const huntItems = scope === "hunt" ? (data.items || []).filter((item) => item.trip_id === "trip_chasse_2026") : (data.items || []);
  const sharedCount = data.balances?.shared_count || 0;
  const lodge = (data.items || []).find((e) => e.id === "exp_chalet_desrochers");

  async function submit(e) {
    e.preventDefault();
    const tripId = scope === "hunt" ? "trip_chasse_2026" : "trip_terrain_3j";
    let receiptUrl = "";
    if (receipt) {
      const body = new FormData();
      body.append("file", receipt);
      body.append("category", "receipt");
      body.append("trip_id", tripId);
      const media = await api("/api/media", { method: "POST", body });
      receiptUrl = media?.url || "";
    }
    await apiJson("/api/expenses", { ...form, amount: Number(form.amount || 0), trip_id: tripId, receipt_url: receiptUrl });
    setReceipt(null);
    setForm((f) => ({ ...f, description: "", amount: "" }));
    await reload();
  }

  return (
    <main className="og-cockpit og-cockpit--expenses" data-testid="page-expenses">
      {scope === "hunt" ? (
        <p data-testid="hunt-bilan-closed">Avant le vendredi 2 octobre, c’est déjà payé. Le nouveau bilan commence ce matin-là, entre les 6 chasseurs.</p>
      ) : null}
      <div className="og-kpi">
        {sharedCount ? (
          <>
            <div className="og-stat"><strong data-testid="exp-total">{data.balances?.total ?? 0}</strong><span>{ui.expenses.total}</span></div>
            <div className="og-stat"><strong data-testid="exp-share">{myShare}</strong><span>{ui.expenses.perPerson}</span></div>
            <div className="og-stat"><strong>{paidByMe}</strong><span>{ui.expenses.paidByMe}</span></div>
            <div className="og-stat"><strong>{balance}</strong><span>{ui.expenses.balance}</span></div>
          </>
        ) : ready ? (
          <p data-testid="no-expense">{ui.expenses.none}</p>
        ) : (
          <p className="og-hint">{ui.app.loading}</p>
        )}
      </div>
      {bilan ? (
        <section className="og-panel" data-testid="prospect-bilan">
          <p className="og-kicker">{ui.hunt.ourCosts}</p>
          <p>{bilan.label}</p>
          {bilan.verified && bilan.per_couple ? <p>{bilan.per_couple} {ui.hunt.perCouple}</p> : null}
        </section>
      ) : null}
      <p className="og-hint" data-testid="split-rule">{ui.expenses.rule}</p>
      {lodge && !lodge.shared ? (
        <section className="og-panel" data-testid="lodge-expense">
          <p>{ui.expenses.pendingLodge} · 568,59 $ · Luc Young</p>
          <button type="button" className="og-btn" onClick={() => api(`/api/expenses/${lodge.id}/include-common`, { method: "POST" }).then(reload)} data-testid="include-lodge">{ui.expenses.includeLodge}</button>
        </section>
      ) : null}
      <div className="og-split-cockpit">
        <section className="og-panel og-scroll">
          <form className="og-inline" onSubmit={submit} data-testid="expense-form">
            <input className="og-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc" placeholder={ui.expenses.description} />
            <input className="og-input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" placeholder={ui.expenses.amount} />
            <select className="og-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {(scope === "hunt" ? HUNT_CATEGORIES.map(([id]) => id) : EXPENSE_CATEGORIES).map((c) => (
                <option key={c} value={c}>{scope === "hunt" ? (HUNT_CATEGORIES.find(([id]) => id === c)?.[1] || c) : (ui.categories[c] || c)}</option>
              ))}
            </select>
            <select className="og-input" value={form.payer_id} onChange={(e) => setForm({ ...form, payer_id: e.target.value })}>
              {(official.length ? official : members).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
            {scope === "hunt" ? <input className="og-input" type="file" accept="image/*" data-testid="expense-receipt" onChange={(event) => setReceipt(event.target.files?.[0] || null)} /> : null}
            <button className="og-btn og-btn--primary" type="submit" data-testid="expense-submit">{ui.expenses.add}</button>
          </form>
          {huntItems.map((e) => (
            <article key={e.id} className="og-expense-row">
              <strong>{localizeDemo(e.description, lang)}</strong>
              <p>{names[e.payer_id] || e.payer_id} · {e.amount}</p>
            </article>
          ))}
        </section>
        <aside className="og-panel og-scroll">
          <h3>{ui.expenses.whoOwes}</h3>
          {scope === "hunt" && actingAsAdmin ? <p data-testid="final-bilan">Faire le bilan final — proposition, à valider avant de la partager.</p> : null}
          {(data.balances?.settlements || []).map((s, i) => (
            <p key={i}>{s.from_name} → {s.to_name} : {s.amount}</p>
          ))}
        </aside>
      </div>
    </main>
  );
}
